import React, { useState, useRef, useEffect } from 'react'
import { Button } from '~/ui/button'
import { Play, Pause, Loader2, Download } from 'lucide-react'

interface AudioPlayerProps {
  audioUrl: string
  title?: string
  className?: string
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, title, className = '' }) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset player state when audioUrl changes
  useEffect(() => {
    setIsPlaying(false)
    setIsLoading(false)
    setError(null)

    const audio = audioRef.current
    if (audio) {
      // When the audioUrl changes, we need to pause any existing playback,
      // reset the time, and then call `load()` to make the <audio> element
      // pick up the new `src`.
      audio.pause()
      audio.currentTime = 0
      // The 'play' button will trigger the actual playback.
    }
  }, [audioUrl])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      setIsLoading(true)
      setError(null)

      audio
        .play()
        .then(() => {
          setIsPlaying(true)
          setIsLoading(false)
        })
        .catch((err) => {
          setError('Failed to play audio')
          setIsLoading(false)
          console.error('Audio playback failed:', err)
        })
    }
  }

  const handleEnded = () => {
    setIsPlaying(false)
  }

  const handleError = () => {
    setError('Audio file could not be loaded')
    setIsLoading(false)
  }

  const downloadAudio = () => {
    const link = document.createElement('a')
    link.href = audioUrl
    link.download = title
      ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp3`
      : 'voice_message.mp3'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleEnded}
        onError={handleError}
        preload="none"
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={togglePlay}
        disabled={isLoading || !!error}
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="mr-2 h-4 w-4" />
        ) : (
          <Play className="mr-2 h-4 w-4" />
        )}
        {isLoading ? 'Loading...' : isPlaying ? 'Pause' : 'Play'}
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={downloadAudio}
        title="Download audio file"
      >
        <Download className="h-4 w-4" />
      </Button>

      {error && <span className="text-sm text-red-600">{error}</span>}
    </div>
  )
}

export default AudioPlayer
